import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useSelector } from 'react-redux';
import Proptypes from 'prop-types';
import ReducerConstants from '../../_shared/constants/reducers';

import BidBox from './BidBoxContainer';
import ConditionsDisclosuresContainer from './ConditionsDisclosuresContainer';
import Divider from './Divider';
import Footer from './Footer';
import Header from './Header';
import HistoryContainer from './HistoryContainer';
import Nav from './Nav';
import OmpDealerProvider from './OmpDealerProvider';
import Overview from './Overview';
import ResearchContainer from './ResearchContainer';
import RetailVdpShare from './RetailVdpShare';
import SellerInfoContainer from './SellerInfoContainer';
import TIAHContainer from './TIAHContainer';
import TransportContainer from './TransportContainer';
import ROUTES from '../Routes/constants';
import ConfirmModal from '../../_shared/components/ConfirmModal';
import { BidStatusName } from '../../_shared/types/auction';
import { COOKIES } from '../../_shared/constants/cookies';
import MixpanelConfig from '../../_shared/constants/mixpanelConfig';
import {
  RetailCreateSource,
  RetailPlatforms
} from '../../_shared/constants/retail';
import { VdpId } from '../../_shared/constants/vdp';
import utmSource from '../../_shared/constants/utmSource';
import useAnalytics, {
  getSellerProperties,
  getVinBasicProperties,
  getVinPostSaleProperties,
  getVinLaunchProperties,
  getVinSaleEventProperties
} from '../../_shared/hooks/useAnalytics';
import useBadgeList from '../../_shared/hooks/useBadgeList';
import useDisclosure from '../../_shared/hooks/useDisclosure';
import useMediaList from '../../_shared/hooks/media/useMediaList';
import useSticky from '../../_shared/hooks/useSticky';
import useTradeDetails from '../../_shared/hooks/trade/useTradeDetails';
import {
  TradeState,
  TradeStateName,
  TradeContextName,
  TradeContext,
  TradeViewVariant
} from '../../_shared/types/trade';
import { getCookie } from '../../_shared/utils/cookies';
import SegmentConfig from '../../_shared/constants/segmentConfig';

import {
  StyledContainer,
  StyledOverviewBidboxContainer,
  StyledTransportSellerContainer,
  StyledTIAHContainer
} from './Styles';
import capitalizeFirstLetter from '../../_shared/utils/capitalizeFirstLetter';

const defaultProps = {
  tradeContext: undefined,
  isOMP: false
};

const propTypes = {
  tradeId: Proptypes.number.isRequired,
  tradeContext: Proptypes.number,
  isOMP: Proptypes.bool
};

const Trade = ({
  tradeId,
  tradeContext,
  isOMP,
  validOmpDealerships,
  handleOmpBidding,
  isOmpMultiOrgDealer = false
}) => {
  const intl = useIntl();
  // TODO: Use useSelect instead

  const tradeDetails = useTradeDetails(tradeId, isOMP, handleOmpBidding);
  const mediaList = useMediaList(tradeId);
  const disclosures = useDisclosure(tradeId, 'all');
  const { isSticky, element } = useSticky();
  const tradeState = tradeDetails?.tradeState;
  const [mpTracked, setMpTracked] = useState(0);
  const badges = useBadgeList(tradeDetails?.dealer?.id);
  const [isVHRModalOpen, setIsVHRModalOpen] = useState(false);
  const track = useAnalytics();
  const user = useSelector(
    state => state[ReducerConstants.AUTH]?.user,
    shallowEqual
  );
  const isCSU = !!(user?.superUserId && user?.superUserId !== user?.id);

  const getSource = () => {
    // TODO: revisit when home and selling pages migrated from tr4 to tr5 (track location-referrer cookie)
    // return one of the following: dashboard fresh trades, dashboard event calendar, search results, to-do/Alerts, list view.
    const pageReferrer = getCookie(COOKIES.LOCATION_REFERRER) || '';

    if (pageReferrer.includes(MixpanelConfig.cookieFlag.dashboardFreshTrades)) {
      return MixpanelConfig.source.dashboardFreshTrades;
    }
    if (
      pageReferrer.includes(MixpanelConfig.cookieFlag.dashboardEventCalendar)
    ) {
      return MixpanelConfig.source.dashboardEventCalendar;
    }
    if (pageReferrer.includes(MixpanelConfig.cookieFlag.toDoOrAlerts)) {
      return MixpanelConfig.source.toDoOrAlerts;
    }
    if (pageReferrer.includes(ROUTES.SEARCH_URL)) {
      return MixpanelConfig.source.searchResults;
    }
    if (pageReferrer.includes(ROUTES.BUYING)) {
      return MixpanelConfig.source.listView;
    }

    return pageReferrer; // return referrer path if source name is not captured
  };

  useEffect(() => {
    setMpTracked(0);
  }, [tradeId]);

  // track mixpanel VDP view event
  useEffect(() => {
    const getOmpEventSourceType = () => {
      if (window.location.search.includes(utmSource.alertsList)) {
        return SegmentConfig.eventSourceType.alertsListView;
      }
      if (window.location.search.includes(utmSource.todoList)) {
        return SegmentConfig.eventSourceType.todoListView;
      }
      if (window.location.search.includes(utmSource.paymentsOnHold)) {
        return SegmentConfig.eventSourceType.paymentsOnHoldListView;
      }

      if (
        window.history?.state?.idx ||
        window.location.search.includes('tab=')
      ) {
        return TradeStateName[tradeState] === TradeStateName[TradeState.pending]
          ? SegmentConfig.eventSourceType.pendingListView
          : SegmentConfig.eventSourceType.browseListView;
      }

      if (
        tradeDetails?.isWatchlisted &&
        (tradeState === TradeState.upcoming || tradeState === TradeState.active)
      ) {
        return SegmentConfig.eventSourceType.watchlistVdp;
      }

      return SegmentConfig.eventSourceType.browseVdp;
    };

    if (
      tradeState &&
      tradeDetails?.auctionSummary &&
      mediaList?.meta?.nonDamagePhotos.length > 0 &&
      mpTracked === 0 &&
      tradeId === tradeDetails.id &&
      track
    ) {
      if (isOMP) {
        const vdpViewType = window.location.pathname.includes(ROUTES.TRADE_URI)
          ? SegmentConfig.vdpViewType.fullPage
          : SegmentConfig.vdpViewType.sideRail;

        track(SegmentConfig.names.vdpViewed, {
          ...getVinBasicProperties(tradeDetails),
          ...getVinLaunchProperties(tradeDetails),
          ...getVinPostSaleProperties(tradeDetails),
          ...getVinSaleEventProperties(tradeDetails),
          ...getSellerProperties(tradeDetails),
          platform_value: capitalizeFirstLetter(RetailPlatforms.Web),
          event_state_type: `${TradeContextName[tradeContext]} ${TradeStateName[tradeState]}`,
          event_source_type: `${getOmpEventSourceType()}`,
          is_timed_event: !!tradeDetails?.saleEventId,
          sc_sale_event_status_type: null,
          vehicle_status_type:
            SegmentConfig.vehicleStatusType[tradeDetails?.tradeState] || null,
          vdp_view_type: vdpViewType
        });

        const urlParams = new URLSearchParams(window.location.search);
        const deepLink = urlParams.get('deepLink');

        if (deepLink === 'emailViewVdp') {
          track(SegmentConfig.names.savedFilterEmailVdpViewed, {
            ...getVinBasicProperties(tradeDetails),
            platform_value: capitalizeFirstLetter(RetailPlatforms.Web),
            vdp_view_type: vdpViewType,
            event_state_type: `${TradeContextName[tradeContext]} ${TradeStateName[tradeState]}`
          });

          // clear queryParam
          const url = new URL(window.location.href);
          url.searchParams.delete('deepLink');
          window.history.replaceState({}, '', url.toString());
        }
      } else {
        track(MixpanelConfig.names.viewVehicleDetailsPage, {
          trade_id: tradeDetails.id,
          auction_id: tradeDetails.auctionSummary?.id,
          vehicle_launched_time: tradeDetails.auctionSummary?.startTime,
          is_floor_met: tradeDetails.tradeFloorPrice?.isFloorPriceMet ?? 'N/A',
          floor_amount: tradeDetails.tradeFloorPrice?.valueDollars,
          year: tradeDetails.year,
          make: tradeDetails.manufacturerName,
          model: tradeDetails.modelName,
          vin: tradeDetails.vin,
          trim: tradeDetails.trim,
          asIs: tradeDetails.isAsIs,
          trade_state: TradeStateName[tradeState],
          trade_state_section: TradeContextName[tradeContext],
          seller_dealer_id: tradeDetails.dealer?.id,
          seller_dealership_name: tradeDetails.dealer?.displayName,
          current_top_bid: tradeDetails.auctionSummary?.maxBid,
          total_bids: tradeDetails.auctionSummary?.numBids,
          in_stock: tradeDetails.isLanded,
          overview_images: mediaList.meta?.count?.photos,
          damage_images: mediaList.meta?.count?.damages,
          videos: mediaList.meta?.count?.videos,
          user_bid_status:
            BidStatusName[tradeDetails.auctionSummary?.userBidStatus],
          source: getSource()
        });
      }

      setMpTracked(1);
    }
  }, [
    track,
    mpTracked,
    tradeDetails,
    tradeContext,
    tradeState,
    mediaList,
    tradeId,
    isOMP
  ]);

  const openRetailCreatePage = source => {
    if (tradeDetails?.vehicleHistoryReportUrl) {
      track(MixpanelConfig.names.initiateRetailVdp, {
        trade_id: tradeDetails.id,
        year: tradeDetails.year,
        make: tradeDetails.manufacturerName,
        model: tradeDetails.modelName,
        vin: tradeDetails.vin,
        trim: tradeDetails.trim,
        asIs: tradeDetails.isAsIs,
        trade_state: TradeStateName[tradeState],
        trade_state_section: TradeContextName[tradeContext],
        source
      });
      window.open(`${ROUTES.RETAIL.replace(':tradeId', tradeId)}`, '_self');
    }
    setIsVHRModalOpen(false);
  };

  const onRetailVDPOpen = source =>
    tradeDetails?.vehicleHistoryReportUrl
      ? openRetailCreatePage(source)
      : setIsVHRModalOpen(true);

  return (
    <StyledContainer id={VdpId} isCSU={isCSU}>
      <OmpDealerProvider
        isOMP={isOMP}
        validOmpDealerships={validOmpDealerships}
        vdpNetwork={tradeDetails.network}
        biddingDealerId={tradeDetails?.auctionSummary?.biddingDealerId}
        tradeDetailsId={tradeDetails?.id}
        isOmpMultiOrgDealer={isOmpMultiOrgDealer}
      >
        <Header
          isWatchlisted={tradeDetails.isWatchlisted}
          statusDates={tradeDetails.statusDates}
          tradeId={tradeId}
          tradeDetails={tradeDetails}
          onRetailVDPOpen={() => onRetailVDPOpen(RetailCreateSource.Header)}
          tradeContext={tradeContext}
          tradeState={tradeState}
          isOMP={isOMP}
        />
        <StyledOverviewBidboxContainer>
          <Overview
            tradeDetails={tradeDetails}
            mediaList={mediaList}
            element={element}
            badges={badges}
            tradeId={tradeId}
            tradeContext={tradeContext}
            tradeState={tradeState}
            isOMP={isOMP}
          />
          <BidBox
            tradeId={tradeId}
            tradeDetails={tradeDetails}
            tradeContext={tradeContext}
            isOMP={isOMP}
            handleOmpBidding={handleOmpBidding}
          />
        </StyledOverviewBidboxContainer>
        {tradeContext === TradeContext.Sell &&
          tradeDetails?.tradeInCustomerInfo && (
            <StyledTIAHContainer>
              <TIAHContainer
                tradeInCustomerInfo={tradeDetails.tradeInCustomerInfo}
                decodedAt={tradeDetails?.decodedAt}
              />
            </StyledTIAHContainer>
          )}
        <Nav
          sticky={isSticky}
          tradeDetails={tradeDetails}
          tradeContext={tradeContext}
          isOmp={isOMP}
        />
        {tradeContext === TradeContext.Sell && (
          <Divider
            text={intl.formatMessage({ id: 'vdp.seller.detailsShownToBuyer' })}
          />
        )}
        <HistoryContainer
          disclosures={disclosures}
          tradeDetails={tradeDetails}
          tradeContext={tradeContext}
          tradeState={tradeState}
        />
        <ConditionsDisclosuresContainer
          disclosures={disclosures}
          tradeDetails={tradeDetails}
          mediaList={mediaList}
          tradeContext={tradeContext}
          tradeState={tradeState}
        />
        <StyledTransportSellerContainer id="conditionDetails">
          <TransportContainer
            tradeId={tradeId}
            tradeDetails={tradeDetails}
            type="details"
            isOMP={isOMP}
          />
          <SellerInfoContainer
            chatCount={tradeDetails.chatStatus?.chatCount}
            dealer={tradeDetails.dealer}
            tradeDetails={tradeDetails}
            tradeId={tradeId}
            badges={badges}
            tradeState={tradeState}
            isOMP={isOMP}
          />
        </StyledTransportSellerContainer>
        <ResearchContainer
          tradeState={tradeState}
          isOMP={isOMP}
          tradeDetails={tradeDetails}
        />
        {tradeContext === TradeContext.Buy && !isOMP && (
          <RetailVdpShare
            onRetailVDPOpen={() => onRetailVDPOpen(RetailCreateSource.Footer)}
          />
        )}
        {!isOMP && tradeState && (
          <Footer tradeContext={tradeContext} tradeState={tradeState} />
        )}
        <ConfirmModal
          isOpen={isVHRModalOpen}
          location={TradeViewVariant.VDP}
          modalLogic={[
            {
              title: intl.formatMessage({ id: 'vdp.retail.noCarfax.title' }),
              msg: intl.formatMessage({ id: 'vdp.retail.noCarfax.message' }),
              onConfirm: () => openRetailCreatePage,
              confirmLabel: intl.formatMessage({ id: 'modal.ok' })
            }
          ]}
        />
      </OmpDealerProvider>
    </StyledContainer>
  );
};

Trade.defaultProps = defaultProps;
Trade.propTypes = propTypes;

export default Trade;
